import { Injectable, OnDestroy } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AuthStatus } from '../../../auth/auth.model';
import { AuthService } from '../../../auth/auth.service';
import { useDispatcher, useStore } from '../../../store.decorator';
import { MsalService } from '../../msal/msal.service';

import { ChatMenuDialogComponent } from './chat-menu-dialog.component';
import { chatMenuDialogReducer } from './chat-menu-dialog.reducer';
import { ChatMenuDialogState } from './chat-menu-dialog.state';
import { ChatNav } from './model/chat-nav.model';
import { ChatRow } from './model/chat-row.model';
import { minMax } from './state/min-max.action';
import { navigate } from './state/navigate.action';

@Injectable({ providedIn: 'root' })
export class ChatMenuDialogService implements OnDestroy {
    state: ChatMenuDialogState;

    /** @deprecated temporary. keep for the time being */
    rows: ChatRow[];

    private _subSub: Subscription;
    private _ref: CmpsDialogRef<ChatMenuDialogComponent, any>;
    private _refSub: Subscription;

    get isOpen(): boolean {
        return this._ref != null && !this._ref.closed;
    }

    constructor(
        private readonly _msalService: MsalService,
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
    ) {
        this._bindAuth();

        useStore(this, 'teamsCall.chatMenu', chatMenuDialogReducer);
    }

    ngOnDestroy(): void {
        this._subSub?.unsubscribe();
        this.close();
    }

    async open<TContext = any>(nav?: ChatNav, ctx?: TContext): Promise<void> {
        useDispatcher(this, navigate({ nav, ctx }));

        if (this.isOpen) {
            return;
        }

        useDispatcher(this, minMax({ minimized: false }));

        const token = await this._msalService.getToken(
            this._authService.user.id,
        );
        if (token == null) {
            return;
        }

        this._refSub?.unsubscribe();
        this._ref = this._dialog.open(ChatMenuDialogComponent, {
            disableOverlayOverflowHidden: true,
            overlayClass: 'cmps-teamscall-chat-menu-dialog--overlay-class',
            portalClass: 'cmps-teamscall-chat-menu-dialog--portal-class',
            portalWrapperClass:
                'cmps-teamscall-chat-menu-dialog--portal-wrapper-class',
            disableBackdrop: true,
            disableClose: true,
            placement: 'bottom-right',
        });
        this._refSub = this._ref.result
            .pipe(
                tap(() => {
                    this._ref = null;
                }),
            )
            .subscribe();
    }

    close(): void {
        this._refSub?.unsubscribe();
        this._ref?.close();
    }

    async toggle(): Promise<void> {
        !this.isOpen ? await this.open() : this.close();
    }

    private _bindAuth(): void {
        this._subSub = this._authService.authStatus
            .pipe(
                tap((authStatus) => {
                    if (authStatus !== AuthStatus.AUTHENTICATED) {
                        this.close();
                    }
                }),
            )
            .subscribe();
    }
}
